import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, User, Building2, IdCard, Mail, Phone, MessageCircle,
  MapPin, Briefcase, ClipboardList, FolderArchive, Wallet, UserCog, Loader2,
} from "lucide-react";
import InformacionTab from "@/pages/modules/clientes/InformacionTab";
import ArchivoTab from "@/pages/modules/clientes/ArchivoTab";
import CobrosTab from "@/pages/modules/clientes/CobrosTab";
import ClienteDialog from "@/pages/modules/clientes/ClienteDialog";

export default function ClienteDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const canEdit = ["super_admin", "admin", "abogado"].includes(user?.role);
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [tab, setTab] = useState(searchParams.get("tab") || "informacion");

  const onTabChange = (v) => {
    setTab(v);
    setSearchParams(v === "informacion" ? {} : { tab: v }, { replace: true });
  };

  const load = () =>
    api.get(`/clientes/${id}`)
      .then((r) => setCliente(r.data))
      .catch(() => toast.error("Error cargando cliente"))
      .finally(() => setLoading(false));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-[#7A1E2E]" />
      </div>
    );
  }
  if (!cliente) return <div className="text-sm text-[#6B5D57]">Cliente no encontrado</div>;

  const initials = (cliente.nombre || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="space-y-8" data-testid="cliente-detalle-page">
      {/* Back */}
      <button
        onClick={() => navigate("/clientes")}
        data-testid="back-to-clientes"
        className="inline-flex items-center gap-2 text-sm text-[#6B5D57] hover:text-[#7A1E2E] transition-colors"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.8} /> Volver a Clientes
      </button>

      {/* Header card */}
      <div className="rounded-xl bg-white border border-[#E5DFD3] p-6 sm:p-8">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="flex items-center gap-5">
            <Avatar className="h-20 w-20 ring-2 ring-[#E5DFD3]">
              <AvatarFallback className={`text-2xl font-semibold ${cliente.tipo === "juridica" ? "bg-[#C19B6C] text-[#2B0913]" : "bg-[#7A1E2E] text-[#F1EDE4]"} font-display`}>
                {cliente.tipo === "juridica" ? <Building2 className="h-8 w-8" strokeWidth={1.6} /> : initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-display text-3xl sm:text-4xl text-[#1A0810]">{cliente.nombre}</h1>
                {cliente.activo ? (
                  <Badge className="bg-[#EEF6EE] text-[#1F5B2E] border border-[#1F5B2E]/20">Activo</Badge>
                ) : (
                  <Badge className="bg-[#F5EEE0] text-[#6B5D57] border border-[#E5DFD3]">Inactivo</Badge>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#6B5D57]">
                <span className="inline-flex items-center gap-1.5">
                  {cliente.tipo === "juridica" ? <Building2 className="h-3.5 w-3.5" strokeWidth={1.8} /> : <User className="h-3.5 w-3.5" strokeWidth={1.8} />}
                  {cliente.tipo === "juridica" ? "Persona jurídica" : "Persona física"}
                </span>
                {cliente.identificacion && (
                  <span className="inline-flex items-center gap-1.5"><IdCard className="h-3.5 w-3.5" strokeWidth={1.8} />{cliente.identificacion}</span>
                )}
                {cliente.telefono && (
                  <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" strokeWidth={1.8} />{cliente.telefono}</span>
                )}
                {cliente.email && (
                  <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" strokeWidth={1.8} />{cliente.email}</span>
                )}
              </div>
            </div>
          </div>
          {canEdit && (
            <Button
              onClick={() => setEditOpen(true)}
              data-testid="cliente-edit-header-btn"
              variant="outline"
              className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]"
            >
              <UserCog className="mr-2 h-4 w-4" strokeWidth={1.8} /> Editar
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={onTabChange}>
        <TabsList className="bg-white border border-[#E5DFD3] p-1 h-auto rounded-full inline-flex">
          <TabsTrigger value="informacion" data-testid="cliente-tab-informacion" className="rounded-full px-5 py-2 gap-2 data-[state=active]:bg-[#7A1E2E] data-[state=active]:text-[#F1EDE4] data-[state=active]:shadow-none text-[#3d2a2a]">
            <ClipboardList className="h-4 w-4" strokeWidth={1.8} /> Información
          </TabsTrigger>
          <TabsTrigger value="archivo" data-testid="cliente-tab-archivo" className="rounded-full px-5 py-2 gap-2 data-[state=active]:bg-[#7A1E2E] data-[state=active]:text-[#F1EDE4] data-[state=active]:shadow-none text-[#3d2a2a]">
            <FolderArchive className="h-4 w-4" strokeWidth={1.8} /> Archivo
          </TabsTrigger>
          <TabsTrigger value="cobros" data-testid="cliente-tab-cobros" className="rounded-full px-5 py-2 gap-2 data-[state=active]:bg-[#7A1E2E] data-[state=active]:text-[#F1EDE4] data-[state=active]:shadow-none text-[#3d2a2a]">
            <Wallet className="h-4 w-4" strokeWidth={1.8} /> Cobros
          </TabsTrigger>
        </TabsList>

        <TabsContent value="informacion" className="mt-6"><InformacionTab cliente={cliente} /></TabsContent>
        <TabsContent value="archivo" className="mt-6"><ArchivoTab clienteId={cliente.id} /></TabsContent>
        <TabsContent value="cobros" className="mt-6"><CobrosTab clienteId={cliente.id} /></TabsContent>
      </Tabs>

      <ClienteDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        editing={cliente}
        onSaved={() => { setEditOpen(false); load(); }}
      />
    </div>
  );
}
